import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserAuth } from '../context/AuthContext';
import {
  Box,
  Center,
  CircularProgress,
  Heading,
  Text,
  Icon,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button
} from '@chakra-ui/react';
import { CheckCircleIcon, WarningIcon } from '@chakra-ui/icons';

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const [error, setError] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Local state for manual input if query params are missing
  const [emailInput, setEmailInput] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [showManualForm, setShowManualForm] = useState(false);

  const { session, verifySignUpCode } = UserAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const verifyEmailFromUrl = async () => {
      // Support Cognito link redirect (usually contains 'code' and 'username' or 'email')
      const code = params.get('code') || params.get('verification_code');
      const email = params.get('username') || params.get('email');

      if (!code || !email) {
        // If query parameters are missing, show the manual input form
        setShowManualForm(true);
        return;
      }

      setLoading(true);
      try {
        const result = await verifySignUpCode(email, code);
        if (result.success) {
          setIsVerified(true);
        } else {
          throw new Error(result.error);
        }
      } catch (err) {
        console.error('Verification error:', err);
        setError('Failed to verify email. The link may have expired or is invalid. Please try entering details manually.');
        setShowManualForm(true);
      } finally {
        setLoading(false);
      }
    };

    verifyEmailFromUrl();
  }, [params, verifySignUpCode]);

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const result = await verifySignUpCode(emailInput, codeInput);
      if (result.success) {
        setIsVerified(true);
      } else {
        setError(result.error?.message || result.error || 'Verification failed.');
      }
    } catch (err) {
      setError(err.message || 'An error occurred during verification.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isVerified) {
      const timeout = setTimeout(() => {
        navigate('/signin');
      }, 3000); 

      return () => clearTimeout(timeout);
    }
  }, [isVerified, navigate]);

  if (isVerified) {
    return (
      <Center mt="20">
        <Box maxW="md" p="6" textAlign="center" bg="white" shadow="md" rounded="lg">
          <VStack spacing={4}>
            <Icon as={CheckCircleIcon} w={12} h={12} color="green.500" />
            <Heading size="md" color="green.500">Email Verified Successfully!</Heading>
            <Text color="gray.600">
              Your email is now verified. Redirecting you to sign in...
            </Text>
          </VStack>
        </Box>
      </Center>
    );
  }

  if (showManualForm) {
    return (
      <Center mt="20">
        <Box maxW="md" w="full" p="6" bg="white" shadow="md" rounded="lg">
          <VStack spacing={4} align="stretch">
            <Center>
              <Heading size="md" color="blue.600">Verify Your Email</Heading>
            </Center>
            {error && (
              <Box p={3} bg="red.50" color="red.600" rounded="md" fontSize="sm">
                {error}
              </Box>
            )}
            <form onSubmit={handleManualSubmit}>
              <FormControl mb={4} isRequired>
                <FormLabel>Email Address</FormLabel>
                <Input
                  type="email"
                  placeholder="yourname@email.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                />
              </FormControl>
              <FormControl mb={4} isRequired>
                <FormLabel>Verification Code</FormLabel>
                <Input
                  type="text"
                  placeholder="123456"
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value)}
                  textAlign="center"
                  letterSpacing="4px"
                  maxLength={6}
                />
              </FormControl>
              <Button
                type="submit"
                isLoading={loading}
                colorScheme="blue"
                w="full"
              >
                Verify Email
              </Button>
            </form>
          </VStack>
        </Box>
      </Center>
    );
  }

  return (
    <Center mt="20">
      <Box maxW="md" p="6" textAlign="center">
        <CircularProgress isIndeterminate color="blue.500" size="48px" />
        <Heading size="md" mt="4">Verifying your email...</Heading>
        <Text mt="2" color="gray.600">Please wait...</Text>
      </Box>
    </Center>
  );
}
